/**
 * PDP のバッジ行（お届け日 / NEW / SALE）。
 *
 * モバイルではバッジを折り返さず横スクロールさせる。お届け日は metafield の自由入力で
 * 長さが読めないため、折り返して縦に伸びるより横に流す方が商品名との距離が変わらない。
 *
 * この Web Component が担うのは「はみ出しているかどうか」の検出だけ。見た目（フェード）と
 * レイアウトは CSS 側の data 属性セレクタで表現する。
 *
 *   data-scrollable       … 横スクロールが発生している
 *   data-can-scroll-right … まだ右に続きがある（＝フェードを掛ける）
 *
 * フェードを「スクロール可能なとき」ではなく「右に続きがあるとき」に出しているのは、
 * 末尾まで送ったあともフェードが残ると、まだ続きがあるように誤読されるため。
 *
 * スクロールできるときだけ tabindex を付けてキーボードでも送れるようにする。近年のブラウザは
 * スクロールコンテナを自動でフォーカス可能にするが、対応状況に差があるため自前で面倒を見る。
 * 常時付けないのは、スクロール不要な通常商品（大半）に無意味なタブ停止を作らないため。
 */
class ProductBadgesYokomo extends HTMLElement {
  #resizeObserver = null;
  #mutationObserver = null;
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    this.addEventListener('scroll', this.#update, { passive: true, signal });

    // 幅の変化（画面回転・リサイズ）
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this);

    // 中身の変化。バリアント切替時、テーマは morph で子要素だけを差し替えるため
    // connectedCallback が再実行されない。SALE バッジの増減を取りこぼさないよう見張る
    this.#mutationObserver = new MutationObserver(this.#update);
    this.#mutationObserver.observe(this, { childList: true, subtree: true, characterData: true });

    this.#update();

    // 幅いっぱいまで伸びたあとに Web フォントが差し替わると、自身のサイズは変わらないまま
    // 中身だけ広がる。ResizeObserver では拾えないので読み込み完了後にもう一度測る
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#update();
    });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#abortController.abort();
  }

  #update = () => {
    // 端数で1pxずれることがあるので、しきい値を持たせて判定する
    const scrollable = this.scrollWidth - this.clientWidth > 1;
    const canScrollRight = scrollable && this.scrollWidth - this.clientWidth - this.scrollLeft > 1;

    this.dataset.scrollable = String(scrollable);
    this.dataset.canScrollRight = String(canScrollRight);

    if (scrollable) {
      this.setAttribute('tabindex', '0');
      this.setAttribute('role', 'group');
      const label = this.dataset.scrollLabel;
      if (label) this.setAttribute('aria-label', label);
    } else {
      this.removeAttribute('tabindex');
      this.removeAttribute('role');
      this.removeAttribute('aria-label');
    }
  };
}

if (!customElements.get('product-badges-yokomo')) {
  customElements.define('product-badges-yokomo', ProductBadgesYokomo);
}
